<template>
  <div
    :class="{
      'upload-file': true,
      'drag-over': isDragOver
    }"
  >
    <input
      v-show="status === 'upload'"
      ref="file"
      type="file"
      class="upload-file-input"
      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      @change="fileHandler"
    >
    <div
      v-show="status === 'upload'"
      class="dropzone"
      @drop.prevent="fileHandler"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragleave"
    >
      <div class="dropzone-icon">
        <img :src="require('@/assets/shipo-sales-plan-icon-upload.svg')">
      </div>
      <span class="dropzone-text-1"><span class="font-semibold text-gray-600">Drag and drop</span> your files here</span>
      <span class="dropzone-text-2">or</span>
      <vs-button
        class="dropzone-button"
        color="primary"
        :disabled="isBrowseDisabled"
        @click="$refs.file.click()"
      >
        Browse
      </vs-button>
    </div>
    <div
      class="progress"
      v-show="status === 'uploading' || status === 'uploaded'"
    >
      <img
        class="progress-icon"
        :src="require('@/assets/shipo-sales-plan-icon-excel.svg')"
      >
      <div
        :class="{
          'progress-body': true,
          'flex-grow': status === 'uploading'
        }"
      >
        <h6 class="progress-filename">
          {{ fileName }}
        </h6>
        <vs-progress
          v-show="status === 'uploading'"
          :percent="progress"
          color="primary"
        >
          primary
        </vs-progress>
        <small class="progress-detail">
          <span class="progress-detail-filesize">{{ fileSize }}</span>
          <span
            v-show="status === 'uploading'"
            class="progress-detail-percent"
          >
            {{ progress }}%
          </span>
        </small>
      </div>
      <vs-button
        v-show="status === 'uploaded' && !autoPreview"
        class="progress-button"
        color="primary"
        @click="$emit('preview')"
      >
        Preview
      </vs-button>
      <vs-button
        v-show="status === 'uploaded'"
        radius
        class="button-remove"
        @click="removeFile"
      >
        <img
          :src="require('@/assets/shipo-sales-plan-icon-trash.svg')"
        >
      </vs-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { convertFileSize } from '@/utils/fileUtils'

export default {
  name: 'UploadFile',
  props: {
    isBrowseDisabled: {
      type: Boolean,
      default: () => false
    },
    autoPreview: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      isDragOver: false
    }
  },
  computed: {
    ...mapGetters({
      status: 'UploadFile/status',
      progress: 'UploadFile/progress',
      fileName: 'UploadFile/fileName',
      fileSize: 'UploadFile/fileSize'
    })
  },
  watch: {
    progress: {
      immediate: true,
      handler(val) {
        switch (true) {
          case (val === 0):
            this.$store.commit('UploadFile/SET_STATUS', 'upload')
            break
          case (val > 0 && val < 100):
            this.$store.commit('UploadFile/SET_STATUS', 'uploading')
            break
          case (val === 100):
            this.$store.commit('UploadFile/SET_STATUS', 'uploaded')
            if (this.autoPreview) {
              this.$emit('preview')
            }
            break
          default:
            this.$store.commit('UploadFile/SET_STATUS', 'uploaded')
            if (this.autoPreview) {
              this.$emit('preview')
            }
            break
        }
      }
    }
  },
  methods: {
    fileHandler(e) {
      const inputValue = e.target.files || e.dataTransfer.files || this.$refs.file.files
      if (inputValue.length === 1 && inputValue[0].name.split('.').pop() === 'xlsx') {
        const file = inputValue[0]
        this.$store.commit('UploadFile/SET_FILE_NAME', file.name)
        this.$store.commit('UploadFile/SET_FILE_SIZE', convertFileSize(file.size))
        this.$emit('upload', inputValue[0])
      } else {
        this.$vs.notify({
          title: 'Failed',
          text: 'Something went wrong',
          color: 'danger',
          icon: 'close',
          position: 'top-right'
        })
      }
      this.isDragOver = false
    },
    onDragOver(e) {
      this.isDragOver = true
      e.dataTransfer.dropEffect = 'move'
      e.preventDefault()
    },
    onDragleave() {
      this.isDragOver = false
    },
    removeFile() {
      this.$refs.file.value = null
      this.$store.dispatch('UploadFile/reset')
      this.$emit('remove')
    }
  }
}
</script>

<style lang="scss" scoped>
  .upload-file {
    @apply border;
    @apply border-primary;
    @apply border-dashed;
    @apply leading-3;
    @apply transition-all;
    @apply duration-500;
    --bg-opacity: 0.36;
    border-radius: 1.2rem;
    background-color: rgba(#CDC6E1, var(--bg-opacity));

    &.drag-over {
      --bg-opacity: 0.66;
    }

    &-input {
      @apply absolute;
      @apply w-0;
      @apply h-0;
      @apply opacity-0;
    }

    .dropzone {
      @apply flex;
      @apply flex-wrap;
      @apply flex-col;
      @apply justify-center;
      @apply items-center;
      @apply py-8;

      &-text-1 {
        @apply text-gray-600;
      }

      &-text-2 {
        @apply text-grey-001;
        @apply mt-5;
        @apply mb-4;
      }

      &-button {
        @apply rounded-xl;
        padding: 1.3rem 4.2rem !important;

        &:focus {
          @apply outline-none;
          @apply border-none;
        }
      }
    }

    .progress {
      @apply flex;
      @apply flex-wrap;
      @apply flex-row;
      @apply justify-start;
      @apply items-center;
      @apply p-8;

      &-icon {
        -webkit-filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.16));
        filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.16));
      }

      &-body {
        @apply flex;
        @apply flex-wrap;
        @apply flex-col;
        @apply ml-6;
      }

      &-filename {
        @apply mb-4;
        @apply font-semibold;
        @apply text-grey;
      }

      /deep/ .vs-progress {
        &--background {
          @apply mb-4;
          height: 9px !important;
        }
      }

      &-detail {
        @apply flex;
        @apply flex-wrap;
        @apply flex-row;
        @apply justify-between;

        &-filesize {
          @apply text-grey-001;
        }

        &-percent {
          @apply text-primary;
          @apply font-semibold;
        }
      }

      &-button {
        @apply ml-8;
        @apply rounded-xl;
        padding: 1.3rem 3.2rem !important;

        &:focus {
          @apply outline-none;
          @apply border-none;
        }
      }

      .button-remove {
        @apply ml-auto;
        @apply bg-transparent  #{!important};

        &:hover {
          @apply shadow-none #{!important};
        }

        &:focus {
          @apply outline-none;
          @apply border-none;
        }
      }
    }
  }
</style>
